import React, { useState, Fragment } from 'react';
import { ArrowUpIcon, ArrowDownIcon, CheckIcon, XIcon, ClockIcon, TruckIcon, BarChart2Icon, ChevronRightIcon } from 'lucide-react';
import { formatCurrency } from '../../utils/currencyFormatter';
import { StatusBadge } from '../../StatusBadge';
export function PurchaseOrderSummary({
  filters,
  locale
}) {
  const [sortField, setSortField] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [expandedPO, setExpandedPO] = useState(null);
  // Sample data
  const purchaseOrders = [{
    id: 'PO-2023-001',
    name: 'Server Hardware',
    vendor: 'Server Solutions Inc.',
    status: 'Sent to Vendor',
    date: '2023-10-10',
    total: 12500.0,
    currency: 'USD',
    items: 5,
    entity: 'Global Technologies Inc.',
    site: 'New York HQ',
    approver: 'John Smith',
    approvalDate: '2023-10-09',
    expectedDelivery: '2023-11-05',
    lineItems: [{
      id: 'ITEM-001',
      description: 'Server Rack',
      quantity: 2,
      unitPrice: 1500,
      total: 3000
    }, {
      id: 'ITEM-002',
      description: 'Server Unit',
      quantity: 3,
      unitPrice: 3000,
      total: 9000
    }, {
      id: 'ITEM-003',
      description: 'Network Switch',
      quantity: 1,
      unitPrice: 500,
      total: 500
    }]
  }, {
    id: 'PO-2023-002',
    name: 'Marketing Materials',
    vendor: 'PrintWorks Inc.',
    status: 'Pending Confirmation',
    date: '2023-10-06',
    total: 3200.5,
    currency: 'USD',
    items: 3,
    entity: 'Global Technologies Inc.',
    site: 'San Francisco Office',
    approver: 'Jane Doe',
    approvalDate: '2023-10-05',
    expectedDelivery: '2023-10-30',
    lineItems: [{
      id: 'ITEM-004',
      description: 'Brochures',
      quantity: 1000,
      unitPrice: 1.5,
      total: 1500
    }, {
      id: 'ITEM-005',
      description: 'Banners',
      quantity: 5,
      unitPrice: 200,
      total: 1000
    }, {
      id: 'ITEM-006',
      description: 'Business Cards',
      quantity: 500,
      unitPrice: 0.75,
      total: 375
    }, {
      id: 'ITEM-007',
      description: 'Stickers',
      quantity: 1000,
      unitPrice: 0.25,
      total: 250
    }]
  }, {
    id: 'PO-2023-003',
    name: 'Office Supplies',
    vendor: 'Office Depot',
    status: 'Partially Delivered',
    date: '2023-09-15',
    total: 1850.75,
    currency: 'USD',
    items: 8,
    entity: 'Global Technologies Inc.',
    site: 'New York HQ',
    approver: 'Michael Johnson',
    approvalDate: '2023-09-14',
    expectedDelivery: '2023-09-30',
    lineItems: [{
      id: 'ITEM-008',
      description: 'Paper',
      quantity: 50,
      unitPrice: 10,
      total: 500
    }, {
      id: 'ITEM-009',
      description: 'Pens',
      quantity: 100,
      unitPrice: 2,
      total: 200
    }, {
      id: 'ITEM-010',
      description: 'Notebooks',
      quantity: 50,
      unitPrice: 5,
      total: 250
    }, {
      id: 'ITEM-011',
      description: 'Desk Organizers',
      quantity: 25,
      unitPrice: 15,
      total: 375
    }, {
      id: 'ITEM-012',
      description: 'Staplers',
      quantity: 15,
      unitPrice: 8,
      total: 120
    }]
  }, {
    id: 'PO-2023-004',
    name: 'IT Equipment',
    vendor: 'Dell Technologies',
    status: 'Delivered',
    date: '2023-09-05',
    total: 8750.0,
    currency: 'USD',
    items: 4,
    entity: 'European Operations Ltd',
    site: 'London Office',
    approver: 'Sarah Williams',
    approvalDate: '2023-09-03',
    expectedDelivery: '2023-09-20',
    lineItems: [{
      id: 'ITEM-013',
      description: 'Laptops',
      quantity: 5,
      unitPrice: 1200,
      total: 6000
    }, {
      id: 'ITEM-014',
      description: 'Monitors',
      quantity: 5,
      unitPrice: 350,
      total: 1750
    }, {
      id: 'ITEM-015',
      description: 'Docking Stations',
      quantity: 5,
      unitPrice: 150,
      total: 750
    }, {
      id: 'ITEM-016',
      description: 'Wireless Keyboards',
      quantity: 5,
      unitPrice: 50,
      total: 250
    }]
  }, {
    id: 'PO-2023-005',
    name: 'Software Licenses',
    vendor: 'Microsoft',
    status: 'Completed',
    date: '2023-08-20',
    total: 5000.0,
    currency: 'USD',
    items: 2,
    entity: 'Global Technologies Inc.',
    site: 'All Sites',
    approver: 'John Smith',
    approvalDate: '2023-08-18',
    expectedDelivery: '2023-08-25',
    lineItems: [{
      id: 'ITEM-017',
      description: 'Office 365 Licenses',
      quantity: 50,
      unitPrice: 60,
      total: 3000
    }, {
      id: 'ITEM-018',
      description: 'Windows Server Licenses',
      quantity: 2,
      unitPrice: 1000,
      total: 2000
    }]
  }, {
    id: 'PO-2023-006',
    name: 'Office Furniture',
    vendor: 'IKEA Business',
    status: 'Cancelled',
    date: '2023-08-10',
    total: 4200.0,
    currency: 'EUR',
    items: 6,
    entity: 'European Operations Ltd',
    site: 'London Office',
    approver: 'Robert Brown',
    approvalDate: '2023-08-08',
    expectedDelivery: '2023-09-10',
    cancelledBy: 'Sarah Williams',
    cancellationDate: '2023-08-15',
    cancellationReason: 'Budget constraints',
    lineItems: [{
      id: 'ITEM-019',
      description: 'Desks',
      quantity: 5,
      unitPrice: 300,
      total: 1500
    }, {
      id: 'ITEM-020',
      description: 'Chairs',
      quantity: 10,
      unitPrice: 150,
      total: 1500
    }, {
      id: 'ITEM-021',
      description: 'Filing Cabinets',
      quantity: 3,
      unitPrice: 200,
      total: 600
    }, {
      id: 'ITEM-022',
      description: 'Bookshelves',
      quantity: 2,
      unitPrice: 150,
      total: 300
    }, {
      id: 'ITEM-023',
      description: 'Conference Table',
      quantity: 1,
      unitPrice: 300,
      total: 300
    }]
  }];
  // Filter purchase orders based on the provided filters
  const filteredPOs = purchaseOrders.filter(po => {
    const poDate = new Date(po.date);
    const startDate = filters.startDate ? new Date(filters.startDate) : null;
    const endDate = filters.endDate ? new Date(filters.endDate) : null;
    const dateInRange = (!startDate || poDate >= startDate) && (!endDate || poDate <= endDate);
    const matchesEntity = !filters.entity || po.entity.includes(filters.entity);
    const matchesSite = !filters.site || po.site.includes(filters.site);
    const matchesVendor = !filters.vendor || po.vendor.includes(filters.vendor);
    const matchesCurrency = !filters.currency || po.currency === filters.currency;
    const matchesStatus = !filters.status || po.status.toLowerCase().includes(filters.status.toLowerCase());
    return dateInRange && matchesEntity && matchesSite && matchesVendor && matchesCurrency && matchesStatus;
  });
  // Sort purchase orders
  const sortedPOs = [...filteredPOs].sort((a, b) => {
    let comparison = 0;
    switch (sortField) {
      case 'id':
        comparison = a.id.localeCompare(b.id);
        break;
      case 'name':
        comparison = a.name.localeCompare(b.name);
        break;
      case 'vendor':
        comparison = a.vendor.localeCompare(b.vendor);
        break;
      case 'status':
        comparison = a.status.localeCompare(b.status);
        break;
      case 'date':
        comparison = new Date(a.date).getTime() - new Date(b.date).getTime();
        break;
      case 'total':
        comparison = a.total - b.total;
        break;
      default:
        comparison = 0;
    }
    return sortDirection === 'asc' ? comparison : -comparison;
  });
  // Calculate summary statistics
  const totalPOs = filteredPOs.length;
  const totalValue = filteredPOs.reduce((sum, po) => sum + po.total, 0);
  const statusCounts = filteredPOs.reduce((counts, po) => {
    counts[po.status] = (counts[po.status] || 0) + 1;
    return counts;
  }, {});
  const vendorCounts = filteredPOs.reduce((counts, po) => {
    counts[po.vendor] = (counts[po.vendor] || 0) + 1;
    return counts;
  }, {});
  const handleSort = field => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  const togglePOExpansion = poId => {
    if (expandedPO === poId) {
      setExpandedPO(null);
    } else {
      setExpandedPO(poId);
    }
  };
  const getStatusBadge = status => {
    switch (status) {
      case 'Sent to Vendor':
        return <StatusBadge status="info" text={status} data-id="element-13633" />;
      case 'Pending Confirmation':
        return <StatusBadge status="warning" text={status} data-id="element-13634" />;
      case 'Partially Delivered':
        return <StatusBadge status="info" text={status} data-id="element-13635" />;
      case 'Delivered':
        return <StatusBadge status="success" text={status} data-id="element-13636" />;
      case 'Completed':
        return <StatusBadge status="success" text={status} data-id="element-13637" />;
      case 'Cancelled':
        return <StatusBadge status="error" text={status} data-id="element-13638" />;
      default:
        return <StatusBadge status="default" text={status} data-id="element-13639" />;
    }
  };
  return <div className="p-6" data-id="element-13640">
      <div className="mb-8" data-id="element-13641">
        <h3 className="text-lg font-medium text-gray-900" data-id="element-13642">
          Purchase Order Summary Report
        </h3>
        <p className="mt-1 text-sm text-gray-500" data-id="element-13643">
          Analysis of purchase orders by status, vendor, and department
        </p>
      </div>
      {/* Summary cards */}
      <div className="p-4 sm:p-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4" data-id="element-13644">
        <div className="bg-white overflow-hidden shadow-sm rounded-lg" data-id="element-13645">
          <div className="p-5" data-id="element-13646">
            <div className="flex items-center" data-id="element-13647">
              <div className="flex-shrink-0" data-id="element-13648">
                <BarChart2Icon className="h-6 w-6 text-gray-400" data-id="element-13649" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-13650">
                <dl data-id="element-13651">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-13652">
                    Total Purchase Orders
                  </dt>
                  <dd className="text-lg font-medium text-gray-900" data-id="element-13653">
                    {totalPOs}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow-sm rounded-lg" data-id="element-13654">
          <div className="p-5" data-id="element-13655">
            <div className="flex items-center" data-id="element-13656">
              <div className="flex-shrink-0" data-id="element-13657">
                <CheckIcon className="h-6 w-6 text-green-400" data-id="element-13658" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-13659">
                <dl data-id="element-13660">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-13661">
                    Completed Orders
                  </dt>
                  <dd className="text-lg font-medium text-gray-900" data-id="element-13662">
                    {statusCounts['Completed'] || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow-sm rounded-lg" data-id="element-13663">
          <div className="p-5" data-id="element-13664">
            <div className="flex items-center" data-id="element-13665">
              <div className="flex-shrink-0" data-id="element-13666">
                <ClockIcon className="h-6 w-6 text-yellow-400" data-id="element-13667" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-13668">
                <dl data-id="element-13669">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-13670">
                    Pending Orders
                  </dt>
                  <dd className="text-lg font-medium text-gray-900" data-id="element-13671">
                    {(statusCounts['Pending Confirmation'] || 0) + (statusCounts['Sent to Vendor'] || 0)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow-sm rounded-lg" data-id="element-13672">
          <div className="p-5" data-id="element-13673">
            <div className="flex items-center" data-id="element-13674">
              <div className="flex-shrink-0" data-id="element-13675">
                <TruckIcon className="h-6 w-6 text-indigo-400" data-id="element-13676" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-13677">
                <dl data-id="element-13678">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-13679">
                    Total Value
                  </dt>
                  <dd className="text-lg font-medium text-gray-900" data-id="element-13680">
                    {formatCurrency(totalValue, 'USD', locale)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Purchase Orders Table */}
      <div className="px-4 py-5 sm:p-6" data-id="element-13681">
        <div className="flex flex-col" data-id="element-13682">
          <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8" data-id="element-13683">
            <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8" data-id="element-13684">
              <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg" data-id="element-13685">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-13686">
                  <thead className="bg-gray-50" data-id="element-13687">
                    <tr data-id="element-13688">
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13689">
                        <button className="group flex items-center space-x-1 text-left focus:outline-none" onClick={() => handleSort('id')} data-id="element-13690">
                          <span data-id="element-13691">PO Number</span>
                          {sortField === 'id' ? sortDirection === 'asc' ? <ArrowUpIcon className="h-4 w-4 text-gray-500" data-id="element-13692" /> : <ArrowDownIcon className="h-4 w-4 text-gray-500" data-id="element-13693" /> : <ArrowUpIcon className="h-4 w-4 text-gray-300 opacity-0 group-hover:opacity-100" data-id="element-13694" />}
                        </button>
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13695">
                        <button className="group flex items-center space-x-1 text-left focus:outline-none" onClick={() => handleSort('name')} data-id="element-13696">
                          <span data-id="element-13697">Description</span>
                          {sortField === 'name' ? sortDirection === 'asc' ? <ArrowUpIcon className="h-4 w-4 text-gray-500" data-id="element-13698" /> : <ArrowDownIcon className="h-4 w-4 text-gray-500" data-id="element-13699" /> : <ArrowUpIcon className="h-4 w-4 text-gray-300 opacity-0 group-hover:opacity-100" data-id="element-13700" />}
                        </button>
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13701">
                        <button className="group flex items-center space-x-1 text-left focus:outline-none" onClick={() => handleSort('vendor')} data-id="element-13702">
                          <span data-id="element-13703">Vendor</span>
                          {sortField === 'vendor' ? sortDirection === 'asc' ? <ArrowUpIcon className="h-4 w-4 text-gray-500" data-id="element-13704" /> : <ArrowDownIcon className="h-4 w-4 text-gray-500" data-id="element-13705" /> : <ArrowUpIcon className="h-4 w-4 text-gray-300 opacity-0 group-hover:opacity-100" data-id="element-13706" />}
                        </button>
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13707">
                        <button className="group flex items-center space-x-1 text-left focus:outline-none" onClick={() => handleSort('status')} data-id="element-13708">
                          <span data-id="element-13709">Status</span>
                          {sortField === 'status' ? sortDirection === 'asc' ? <ArrowUpIcon className="h-4 w-4 text-gray-500" data-id="element-13710" /> : <ArrowDownIcon className="h-4 w-4 text-gray-500" data-id="element-13711" /> : <ArrowUpIcon className="h-4 w-4 text-gray-300 opacity-0 group-hover:opacity-100" data-id="element-13712" />}
                        </button>
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13713">
                        <button className="group flex items-center space-x-1 text-left focus:outline-none" onClick={() => handleSort('date')} data-id="element-13714">
                          <span data-id="element-13715">Date</span>
                          {sortField === 'date' ? sortDirection === 'asc' ? <ArrowUpIcon className="h-4 w-4 text-gray-500" data-id="element-13716" /> : <ArrowDownIcon className="h-4 w-4 text-gray-500" data-id="element-13717" /> : <ArrowUpIcon className="h-4 w-4 text-gray-300 opacity-0 group-hover:opacity-100" data-id="element-13718" />}
                        </button>
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13719">
                        <button className="group flex items-center space-x-1 text-left focus:outline-none" onClick={() => handleSort('total')} data-id="element-13720">
                          <span data-id="element-13721">Total</span>
                          {sortField === 'total' ? sortDirection === 'asc' ? <ArrowUpIcon className="h-4 w-4 text-gray-500" data-id="element-13722" /> : <ArrowDownIcon className="h-4 w-4 text-gray-500" data-id="element-13723" /> : <ArrowUpIcon className="h-4 w-4 text-gray-300 opacity-0 group-hover:opacity-100" data-id="element-13724" />}
                        </button>
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13725">
                        <span data-id="element-13726">Items</span>
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13727">
                        <span data-id="element-13728">Details</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-13729">
                    {sortedPOs.map(po => <Fragment key={po.id} data-id="element-13730">
                        <tr className={expandedPO === po.id ? 'bg-indigo-50' : 'hover:bg-gray-50'} data-id="element-13731">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-indigo-600" data-id="element-13732">
                            {po.id}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-13733">
                            {po.name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-13734">
                            {po.vendor}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap" data-id="element-13735">
                            {getStatusBadge(po.status)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-13736">
                            {new Date(po.date).toLocaleDateString(locale)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-13737">
                            {formatCurrency(po.total, po.currency, locale)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-13738">
                            {po.items}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-13739">
                            <button onClick={() => togglePOExpansion(po.id)} className="text-indigo-600 hover:text-indigo-900 flex items-center" data-id="element-13740">
                              <span data-id="element-13741">Details</span>
                              <ChevronRightIcon className={`ml-1 h-5 w-5 transform transition-transform ${expandedPO === po.id ? 'rotate-90' : ''}`} data-id="element-13742" />
                            </button>
                          </td>
                        </tr>
                        {expandedPO === po.id && <tr data-id="element-13743">
                            <td colSpan={8} className="px-6 py-4 bg-gray-50" data-id="element-13744">
                              <div className="border rounded-md p-4 bg-white" data-id="element-13745">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-13746">
                                  <div data-id="element-13747">
                                    <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1" data-id="element-13748">
                                      Entity / Site
                                    </h4>
                                    <p className="text-sm text-gray-900" data-id="element-13749">
                                      {po.entity} - {po.site}
                                    </p>
                                  </div>
                                  <div data-id="element-13750">
                                    <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1" data-id="element-13751">
                                      Approver
                                    </h4>
                                    <p className="text-sm text-gray-900" data-id="element-13752">
                                      {po.approver} (
                                      {new Date(po.approvalDate).toLocaleDateString(locale)}
                                      )
                                    </p>
                                  </div>
                                  <div data-id="element-13753">
                                    <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1" data-id="element-13754">
                                      Expected Delivery
                                    </h4>
                                    <p className="text-sm text-gray-900" data-id="element-13755">
                                      {new Date(po.expectedDelivery).toLocaleDateString(locale)}
                                    </p>
                                  </div>
                                </div>
                                {po.status === 'Cancelled' && <div className="mb-4 p-3 bg-red-50 rounded-md" data-id="element-13756">
                                    <h4 className="text-xs font-medium text-red-800 uppercase tracking-wider mb-1" data-id="element-13757">
                                      Cancellation Details
                                    </h4>
                                    <p className="text-sm text-red-800" data-id="element-13758">
                                      Cancelled by {po.cancelledBy} on{' '}
                                      {new Date(po.cancellationDate).toLocaleDateString(locale)}
                                    </p>
                                    <p className="text-sm text-red-800" data-id="element-13759">
                                      Reason: {po.cancellationReason}
                                    </p>
                                  </div>}
                                <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-13760">
                                  Line Items
                                </h4>
                                <div className="overflow-x-auto" data-id="element-13761">
                                  <table className="min-w-full divide-y divide-gray-200" data-id="element-13762">
                                    <thead className="bg-gray-50" data-id="element-13763">
                                      <tr data-id="element-13764">
                                        <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13765">
                                          Item ID
                                        </th>
                                        <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13766">
                                          Description
                                        </th>
                                        <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13767">
                                          Quantity
                                        </th>
                                        <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13768">
                                          Unit Price
                                        </th>
                                        <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13769">
                                          Total
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200" data-id="element-13770">
                                      {po.lineItems.map(item => <tr key={item.id} data-id="element-13771">
                                          <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500" data-id="element-13772">
                                            {item.id}
                                          </td>
                                          <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900" data-id="element-13773">
                                            {item.description}
                                          </td>
                                          <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500" data-id="element-13774">
                                            {item.quantity}
                                          </td>
                                          <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500" data-id="element-13775">
                                            {formatCurrency(item.unitPrice, po.currency, locale)}
                                          </td>
                                          <td className="px-3 py-2 whitespace-nowrap text-xs font-medium text-gray-900" data-id="element-13776">
                                            {formatCurrency(item.total, po.currency, locale)}
                                          </td>
                                        </tr>)}
                                    </tbody>
                                    <tfoot data-id="element-13777">
                                      <tr className="bg-gray-50" data-id="element-13778">
                                        <td colSpan={4} className="px-3 py-2 text-xs font-medium text-gray-500 text-right" data-id="element-13779">
                                          Total:
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap text-xs font-medium text-gray-900" data-id="element-13780">
                                          {formatCurrency(po.total, po.currency, locale)}
                                        </td>
                                      </tr>
                                    </tfoot>
                                  </table>
                                </div>
                              </div>
                            </td>
                          </tr>}
                      </Fragment>)}
                  </tbody>
                </table>
                {sortedPOs.length === 0 && <div className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-13781">
                    No purchase orders found matching the selected filters.
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Summary section */}
      <div className="px-4 py-5 sm:p-6 border-t border-gray-200" data-id="element-13782">
        <h4 className="text-base font-medium text-gray-900 mb-4" data-id="element-13783">
          Summary by Status
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-id="element-13784">
          {Object.entries(statusCounts).map(([status, count]) => <div key={status} className="bg-white overflow-hidden shadow-sm rounded-lg p-4" data-id="element-13785">
              <div className="text-sm font-medium text-gray-500" data-id="element-13786">{status}</div>
              <div className="mt-1 text-2xl font-semibold text-gray-900" data-id="element-13787">
                {count}
              </div>
              <div className="mt-1 text-xs text-gray-500" data-id="element-13788">
                {Math.round(count / totalPOs * 100)}% of total
              </div>
            </div>)}
        </div>
      </div>
    </div>;
}